import React, { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { Lock, Mail, User, ShieldCheck, ArrowLeft, Eye, EyeOff } from "lucide-react";
import { GoogleOAuthProvider, useGoogleLogin } from "@react-oauth/google";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";
import api from "../lib/api";
import { loadFbSdk } from "../lib/fbSdk";
import { hydrateRequestOptions, serialiseAssertion, isWebAuthnSupported } from "../lib/webauthn";

const GoogleIcon = () => (
    <svg className="w-5 h-5" viewBox="0 0 24 24">
        <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
        <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
        <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
        <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
    </svg>
);

// Lives inside GoogleOAuthProvider — hook can only be called there.
const GoogleSignInButton = ({ onSuccess, onError, disabled }) => {
    const login = useGoogleLogin({
        flow: "auth-code",
        onSuccess,
        onError,
        // Request the email + profile scopes needed by the backend.
        scope: "openid email profile",
    });
    return (
        <button
            type="button"
            onClick={() => login()}
            disabled={disabled}
            data-testid="login-google-btn"
            className="w-full flex items-center justify-center gap-3 border border-black/10 rounded-full py-3.5 font-medium text-[#1C252E] hover:bg-[#F4F1EA] transition-all disabled:opacity-60"
        >
            <GoogleIcon />
            Continue with Google
        </button>
    );
};

const Login = () => {
    const navigate = useNavigate();
    const { loginUser, registerUser, requestMagicLink, loginFacebookOAuth, completeWebAuthn, loginGoogleOAuth } = useAuth();
    const [mode, setMode] = useState("signin"); // "signin" | "signup" | "magic"
    const [email, setEmail] = useState("");
    const [password, setPassword] = useState("");
    const [name, setName] = useState("");
    const [street, setStreet] = useState("");
    const [city, setCity] = useState("");
    const [state, setState] = useState("");
    const [zip, setZip] = useState("");
    const [signupSent, setSignupSent] = useState(false);
    const [magicSent, setMagicSent] = useState(false);
    const [loading, setLoading] = useState(false);
    const [showPassword, setShowPassword] = useState(false);
    const [pending2fa, setPending2fa] = useState(null); // { pending_2fa_token, email, name }
    const [tapping, setTapping] = useState(false);
    const [googleClientId, setGoogleClientId] = useState("");
    const [facebookAppId, setFacebookAppId] = useState("");

    useEffect(() => {
        // Fetch the active Google OAuth Client ID. Empty string = Google login disabled.
        api.get("/auth/google/config")
            .then((r) => setGoogleClientId(r.data?.client_id || ""))
            .catch(() => setGoogleClientId(""));
        api.get("/auth/facebook/config")
            .then((r) => setFacebookAppId(r.data?.app_id || ""))
            .catch(() => setFacebookAppId(""));
    }, []);

    const handleGoogleSuccess = async (codeResponse) => {
        setLoading(true);
        try {
            const data = await loginGoogleOAuth(codeResponse.code);
            toast.success(`Welcome, ${data?.user?.name || data?.user?.email || "friend"}!`);
            navigate("/");
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Google sign-in failed");
        } finally {
            setLoading(false);
        }
    };

    const handleGoogleError = () => {
        toast.error("Google sign-in was cancelled or failed");
    };

    const handleFacebookLogin = async () => {
        if (!facebookAppId) {
            toast.error("Facebook sign-in is not configured");
            return;
        }
        setLoading(true);
        try {
            const FB = await loadFbSdk(facebookAppId);
            // Wrap FB.login (callback-based) in a Promise.
            const fbResp = await new Promise((resolve) => {
                FB.login((response) => resolve(response), { scope: "email,public_profile" });
            });
            if (fbResp.status !== "connected" || !fbResp.authResponse?.accessToken) {
                if (fbResp.status === "not_authorized") {
                    toast.error("You declined the permission request. Facebook sign-in cancelled.");
                } else {
                    toast.error("Facebook sign-in cancelled");
                }
                setLoading(false);
                return;
            }
            const data = await loginFacebookOAuth(fbResp.authResponse.accessToken);
            toast.success(`Welcome, ${data?.user?.name || data?.user?.email || "friend"}!`);
            navigate("/");
        } catch (err) {
            toast.error(err?.response?.data?.detail || err?.message || "Facebook sign-in failed");
        } finally {
            setLoading(false);
        }
    };

    const handleEmailSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);
        try {
            if (mode === "magic") {
                await requestMagicLink(email);
                setMagicSent(true);
                return;
            }
            if (mode === "signup") {
                const data = await registerUser({
                    email, name,
                    street, city, state, zip_code: zip,
                });
                if (data?.email_sent) {
                    setSignupSent(true);
                    return;
                }
                toast.success(`Welcome to Reddog, ${data.name}!`);
                navigate(["admin", "board"].includes(data.role) ? "/admin" : "/account");
                return;
            }
            const data = await loginUser(email, password);
            if (data?.requires_2fa) {
                setPending2fa({ pending_2fa_token: data.pending_2fa_token, email: data.email, name: data.name });
                toast.message("Tap your security key to finish signing in.");
                return;
            }
            toast.success(`Welcome back, ${data.name}`);
            navigate(["admin", "board"].includes(data.role) ? "/admin" : "/account");
        } catch (err) {
            const detail = err?.response?.data?.detail;
            toast.error(typeof detail === "string" ? detail : (mode === "signup" ? "Could not create account" : mode === "magic" ? "Could not send link" : "Login failed"));
        } finally {
            setLoading(false);
        }
    };

    const submitWebAuthn = async () => {
        if (!pending2fa) return;
        if (!isWebAuthnSupported()) {
            toast.error("Your browser doesn't support WebAuthn / security keys.");
            return;
        }
        setTapping(true);
        try {
            const { data: begin } = await api.post("/webauthn/auth/begin", { pending_2fa_token: pending2fa.pending_2fa_token });
            const publicKey = hydrateRequestOptions(JSON.parse(begin.options));
            const assertion = await navigator.credentials.get({ publicKey });
            if (!assertion) throw new Error("No assertion returned.");
            const { data: complete } = await api.post("/webauthn/auth/complete", {
                challenge_token: begin.challenge_token,
                assertion: serialiseAssertion(assertion),
            });
            completeWebAuthn(complete);
            toast.success(`Welcome back, ${complete.name || complete.email}`);
            navigate(["admin", "board"].includes(complete.role) ? "/admin" : "/account");
        } catch (err) {
            const detail = err?.response?.data?.detail;
            if (err?.name === "NotAllowedError") {
                toast.error("Security-key prompt cancelled. Try again.");
            } else {
                toast.error(typeof detail === "string" ? detail : (err?.message || "Security-key verification failed."));
            }
        } finally {
            setTapping(false);
        }
    };

    const passkeyLogin = async () => {
        if (!isWebAuthnSupported()) {
            toast.error("Your browser doesn't support WebAuthn / security keys.");
            return;
        }
        const target = email.trim();
        setTapping(true);
        try {
            // If the email field is empty, use the discoverable-credentials flow
            // (true 1-tap — browser shows its own credential picker).
            // If filled, fall back to the email-targeted donor passkey path.
            let begin;
            if (target) {
                const r = await api.post("/webauthn/passkey/begin", { email: target });
                begin = r.data;
            } else {
                const r = await api.post("/webauthn/passkey/discoverable/begin");
                begin = r.data;
            }
            const publicKey = hydrateRequestOptions(JSON.parse(begin.options));
            const assertion = await navigator.credentials.get({ publicKey });
            if (!assertion) throw new Error("No assertion returned.");
            const { data: complete } = await api.post("/webauthn/auth/complete", {
                challenge_token: begin.challenge_token,
                assertion: serialiseAssertion(assertion),
            });
            completeWebAuthn(complete);
            toast.success(`Welcome back, ${complete.name || complete.email}`);
            navigate(["admin", "board"].includes(complete.role) ? "/admin" : "/account");
        } catch (err) {
            const detail = err?.response?.data?.detail;
            if (err?.name === "NotAllowedError") {
                toast.error("Security-key prompt cancelled. Try again.");
            } else {
                toast.error(typeof detail === "string" ? detail : "No passkey available. Sign in with your password instead.");
            }
        } finally {
            setTapping(false);
        }
    };

    const isSignup = mode === "signup";
    const isMagic = mode === "magic";

    // "Check your email" confirmation screens
    if (signupSent || magicSent) {
        const isNew = signupSent;
        return (
            <div className="min-h-[calc(100vh-160px)] flex items-center justify-center px-6 py-16" data-testid="login-magic-sent">
                <div className="w-full max-w-md">
                    <div className="text-center mb-8">
                        <div className="w-14 h-14 rounded-full bg-[#F2D8D5] flex items-center justify-center mx-auto mb-5">
                            <Mail className="w-6 h-6 text-[#E53935]" strokeWidth={1.5} />
                        </div>
                        <h1 className="font-heading text-3xl text-[#1C252E] font-medium tracking-tight">
                            {isNew ? "Welcome aboard!" : "Check your inbox"}
                        </h1>
                        <p className="text-sm text-[#4A5568] mt-3 leading-relaxed">
                            We just emailed <span className="font-medium text-[#1C252E]">{email}</span> a one-click link{" "}
                            {isNew ? "to verify your email and set your password." : "to sign you in."}
                            &nbsp;It&apos;s valid for 60 minutes.
                        </p>
                    </div>
                    <div className="bg-white border border-black/5 rounded-3xl p-6 space-y-3">
                        <p className="text-xs text-[#4A5568] leading-relaxed">
                            Didn&apos;t get it? Check spam, or wait a moment and{" "}
                            <button
                                type="button"
                                onClick={async () => {
                                    try { await requestMagicLink(email); toast.success("Sent again"); }
                                    catch { toast.error("Couldn't resend"); }
                                }}
                                className="text-[#E53935] underline"
                                data-testid="login-magic-resend-btn"
                            >
                                resend
                            </button>.
                        </p>
                        <button
                            type="button"
                            onClick={() => { setSignupSent(false); setMagicSent(false); setMode("signin"); }}
                            className="w-full inline-flex items-center justify-center gap-2 text-sm text-[#4A5568] hover:text-[#1C252E] pt-2"
                        >
                            <ArrowLeft className="w-4 h-4" strokeWidth={1.5} /> Back to sign-in
                        </button>
                    </div>
                </div>
            </div>
        );
    }

    if (pending2fa) {
        return (
            <div className="min-h-[calc(100vh-160px)] flex items-center justify-center px-6 py-16" data-testid="login-2fa-step">
                <div className="w-full max-w-md">
                    <div className="text-center mb-10">
                        <p className="text-xs tracking-[0.18em] uppercase font-bold text-[#E53935] mb-3">Two-factor required</p>
                        <h1 className="font-heading text-4xl text-[#1C252E] font-medium tracking-tight" data-testid="login-2fa-heading">
                            Tap your security key
                        </h1>
                        <p className="text-sm text-[#4A5568] mt-3">
                            Signed in as <span className="font-medium text-[#1C252E]">{pending2fa.email}</span>.
                            Plug in your FIDO2 key and tap the gold disc when your browser prompts you.
                        </p>
                    </div>
                    <div className="bg-white border border-black/5 rounded-3xl p-8 space-y-4">
                        <div className="flex items-center justify-center py-6">
                            <div className="w-20 h-20 rounded-full bg-[#F4F1EA] flex items-center justify-center animate-pulse">
                                <ShieldCheck className="w-10 h-10 text-[#E53935]" strokeWidth={1.5} />
                            </div>
                        </div>
                        <button
                            type="button"
                            onClick={submitWebAuthn}
                            disabled={tapping}
                            data-testid="login-2fa-submit-btn"
                            className="w-full bg-[#E53935] hover:bg-[#D32F2F] text-white rounded-full py-3.5 font-medium transition-all disabled:opacity-60"
                        >
                            {tapping ? "Waiting for your key…" : "Use my security key"}
                        </button>
                        <button
                            type="button"
                            onClick={() => { setPending2fa(null); setPassword(""); }}
                            data-testid="login-2fa-cancel-btn"
                            className="w-full inline-flex items-center justify-center gap-2 text-sm text-[#4A5568] hover:text-[#1C252E]"
                        >
                            <ArrowLeft className="w-4 h-4" strokeWidth={1.5} /> Use a different account
                        </button>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-[calc(100vh-160px)] flex items-center justify-center px-6 py-16" data-testid="login-page">
            <div className="w-full max-w-md">
                <div className="text-center mb-10">
                    <p className="text-xs tracking-[0.18em] uppercase font-bold text-[#E53935] mb-3">
                        Welcome to Reddog
                    </p>
                    <h1 className="font-heading text-4xl text-[#1C252E] font-medium tracking-tight" data-testid="login-heading">
                        {isSignup ? "Create your account" : isMagic ? "Sign in with a magic link" : "Sign in"}
                    </h1>
                    <p className="text-sm text-[#4A5568] mt-3">
                        {isMagic ? "We'll email you a one-click link — no password needed." : <>Or <Link to="/checkout" className="text-[#E53935] hover:underline">checkout as guest</Link></>}
                    </p>
                </div>

                <div className="bg-white border border-black/5 rounded-3xl p-8 space-y-4">
                    {googleClientId && (
                        <GoogleOAuthProvider clientId={googleClientId}>
                            <GoogleSignInButton onSuccess={handleGoogleSuccess} onError={handleGoogleError} disabled={loading} />
                        </GoogleOAuthProvider>
                    )}

                    {facebookAppId && (
                        <button
                            onClick={handleFacebookLogin}
                            disabled={loading}
                            data-testid="login-facebook-btn"
                            className="w-full flex items-center justify-center gap-3 bg-[#1877F2] hover:bg-[#145DB2] text-white rounded-full py-3.5 font-medium transition-all disabled:opacity-60"
                        >
                            <svg className="w-5 h-5" fill="white" viewBox="0 0 24 24">
                                <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
                            </svg>
                            Continue with Facebook
                        </button>
                    )}

                    {(googleClientId || facebookAppId) && (
                        <div className="relative my-6">
                            <div className="absolute inset-0 flex items-center">
                                <span className="w-full border-t border-black/10" />
                            </div>
                            <div className="relative flex justify-center">
                                <span className="bg-white px-4 text-xs uppercase tracking-wider text-[#4A5568]">
                                    Or with email
                            </span>
                        </div>
                    </div>
                    )}

                    <form onSubmit={handleEmailSubmit} className="space-y-4">
                        {isSignup && (
                            <>
                                <div className="relative">
                                    <User className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-[#4A5568]" strokeWidth={1.5} />
                                    <label htmlFor="signup-name" className="sr-only">Full name</label>
                                    <input
                                        id="signup-name"
                                        type="text"
                                        placeholder="Full name"
                                        autoComplete="name"
                                        value={name}
                                        onChange={(e) => setName(e.target.value)}
                                        required
                                        minLength={1}
                                        maxLength={80}
                                        data-testid="login-name-input"
                                        className="w-full rounded-xl border border-black/10 pl-11 pr-4 py-3 bg-white focus:outline-none focus:ring-2 focus:ring-[#E53935]/40 focus:border-[#E53935] transition-all"
                                    />
                                </div>
                                <div>
                                    <label htmlFor="signup-street" className="sr-only">Street address</label>
                                    <input
                                        id="signup-street"
                                        type="text"
                                        placeholder="Street address"
                                        autoComplete="street-address"
                                        value={street}
                                        onChange={(e) => setStreet(e.target.value)}
                                        required
                                        maxLength={200}
                                        data-testid="login-street-input"
                                        className="w-full rounded-xl border border-black/10 px-4 py-3 bg-white focus:outline-none focus:ring-2 focus:ring-[#E53935]/40 focus:border-[#E53935] transition-all"
                                    />
                                </div>
                                <div className="grid grid-cols-3 gap-2">
                                    <input
                                        type="text"
                                        placeholder="City"
                                        autoComplete="address-level2"
                                        value={city}
                                        onChange={(e) => setCity(e.target.value)}
                                        required
                                        maxLength={80}
                                        data-testid="login-city-input"
                                        className="rounded-xl border border-black/10 px-3 py-3 bg-white focus:outline-none focus:ring-2 focus:ring-[#E53935]/40 focus:border-[#E53935] transition-all"
                                    />
                                    <input
                                        type="text"
                                        placeholder="State"
                                        autoComplete="address-level1"
                                        value={state}
                                        onChange={(e) => setState(e.target.value)}
                                        required
                                        maxLength={40}
                                        data-testid="login-state-input"
                                        className="rounded-xl border border-black/10 px-3 py-3 bg-white focus:outline-none focus:ring-2 focus:ring-[#E53935]/40 focus:border-[#E53935] transition-all"
                                    />
                                    <input
                                        type="text"
                                        placeholder="ZIP"
                                        autoComplete="postal-code"
                                        value={zip}
                                        onChange={(e) => setZip(e.target.value)}
                                        required
                                        maxLength={15}
                                        data-testid="login-zip-input"
                                        className="rounded-xl border border-black/10 px-3 py-3 bg-white focus:outline-none focus:ring-2 focus:ring-[#E53935]/40 focus:border-[#E53935] transition-all"
                                    />
                                </div>
                            </>
                        )}
                        <div className="relative">
                            <Mail className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-[#4A5568]" strokeWidth={1.5} />
                            <label htmlFor="login-email" className="sr-only">Email</label>
                            <input
                                id="login-email"
                                type="email"
                                placeholder="Email address"
                                autoComplete="email"
                                value={email}
                                onChange={(e) => setEmail(e.target.value)}
                                required
                                data-testid="login-email-input"
                                className="w-full rounded-xl border border-black/10 pl-11 pr-4 py-3 bg-white focus:outline-none focus:ring-2 focus:ring-[#E53935]/40 focus:border-[#E53935] transition-all"
                            />
                        </div>
                        {!isSignup && !isMagic && (
                        <div className="relative">
                            <Lock className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-[#4A5568]" strokeWidth={1.5} />
                            <label htmlFor="login-password" className="sr-only">Password</label>
                            <input
                                id="login-password"
                                type={showPassword ? "text" : "password"}
                                placeholder="Password"
                                autoComplete="current-password"
                                value={password}
                                onChange={(e) => setPassword(e.target.value)}
                                required
                                data-testid="login-password-input"
                                className="w-full rounded-xl border border-black/10 pl-11 pr-12 py-3 bg-white focus:outline-none focus:ring-2 focus:ring-[#E53935]/40 focus:border-[#E53935] transition-all"
                            />
                            <button
                                type="button"
                                onClick={() => setShowPassword((v) => !v)}
                                aria-label={showPassword ? "Hide password" : "Show password"}
                                aria-pressed={showPassword}
                                data-testid="login-password-toggle"
                                className="absolute right-3 top-1/2 -translate-y-1/2 p-1.5 rounded-full text-[#4A5568] hover:text-[#1C252E] hover:bg-[#F4F1EA] transition-colors"
                            >
                                {showPassword
                                    ? <EyeOff className="w-4 h-4" strokeWidth={1.5} />
                                    : <Eye className="w-4 h-4" strokeWidth={1.5} />}
                            </button>
                        </div>
                        )}
                        {isSignup && (
                            <p className="text-xs text-[#4A5568] leading-relaxed" data-testid="login-passwordless-hint">
                                No password to pick right now — we&apos;ll email you a link to verify your email and set one on first sign-in.
                            </p>
                        )}
                        <button
                            type="submit"
                            disabled={loading}
                            data-testid="login-submit-btn"
                            className="w-full bg-[#1C252E] hover:bg-black text-white rounded-full py-3.5 font-medium transition-all disabled:opacity-60"
                        >
                            {loading
                                ? (isSignup ? "Creating…" : isMagic ? "Sending…" : "Signing in…")
                                : (isSignup ? "Create account" : isMagic ? "Email me a magic link" : "Sign in")}
                        </button>
                        {!isSignup && !isMagic && (
                            <div className="flex items-center justify-between pt-1">
                                <button
                                    type="button"
                                    onClick={() => setMode("magic")}
                                    data-testid="login-magic-mode-btn"
                                    className="text-sm text-[#4A5568] hover:text-[#E53935] hover:underline"
                                >
                                    Sign in with a magic link
                                </button>
                                <Link
                                    to="/forgot-password"
                                    data-testid="login-forgot-password-link"
                                    className="text-sm text-[#4A5568] hover:text-[#E53935] hover:underline"
                                >
                                    Forgot password?
                                </Link>
                            </div>
                        )}
                        {isMagic && (
                            <button
                                type="button"
                                onClick={() => setMode("signin")}
                                data-testid="login-magic-back-btn"
                                className="w-full text-center text-sm text-[#4A5568] hover:text-[#1C252E]"
                            >
                                Use my password instead
                            </button>
                        )}
                    </form>

                    {!isSignup && isWebAuthnSupported() && (
                        <div className="space-y-1.5">
                            <button
                                type="button"
                                onClick={passkeyLogin}
                                disabled={loading || tapping}
                                data-testid="login-passkey-btn"
                                className="w-full inline-flex items-center justify-center gap-2 border border-[#E53935]/30 text-[#1C252E] hover:bg-[#F2D8D5]/40 rounded-full py-3 font-medium text-sm transition-all disabled:opacity-60"
                            >
                                <ShieldCheck className="w-4 h-4 text-[#E53935]" strokeWidth={1.5} />
                                {tapping ? "Tap your key…" : "Sign in with security key"}
                            </button>
                            <p className="text-center text-[11px] text-[#4A5568]">
                                Leave email blank for a 1-tap passkey sign-in.
                            </p>
                        </div>
                    )}

                    <p className="text-center text-sm text-[#4A5568] pt-2">
                        {isSignup ? "Already have an account?" : "Don't have an account?"}{" "}
                        <button
                            type="button"
                            onClick={() => setMode(isSignup ? "signin" : "signup")}
                            data-testid="login-toggle-mode-btn"
                            className="text-[#E53935] hover:underline font-medium"
                        >
                            {isSignup ? "Sign in" : "Create one"}
                        </button>
                    </p>
                </div>
            </div>
        </div>
    );
};

export default Login;
